import { create } from 'zustand';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'lizrd-theme';

/** The initial theme is stamped on <html> before paint by the inline script in
 * index.html (light-first). Read it back so the store and the DOM never disagree. */
function initialTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

/** Applies the theme to the DOM and persists the choice. */
function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* storage unavailable (e.g. private mode) — the theme still applies for the session */
  }
}

/** Client-side UI state (Frontend §State Management: UI state). */
interface UiState {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  /** The domain whose sync the global SyncBanner should track. Set whenever
   * a sync is triggered (onboarding or the app's "Sync now"); the banner clears
   * it once the sync settles. */
  activeSyncEnvId: string | null;
  setActiveSyncEnv: (envId: string) => void;
  clearActiveSyncEnv: () => void;
}

export const useUiStore = create<UiState>((set, get) => ({
  theme: initialTheme(),
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),

  activeSyncEnvId: null,
  setActiveSyncEnv: (envId) => set({ activeSyncEnvId: envId }),
  clearActiveSyncEnv: () => set({ activeSyncEnvId: null }),
}));
